import React, {MutableRefObject, useContext, useEffect, useRef, useState} from "react";
import JobDisplay from "../../controllers/JobDisplay";
import {dateFormat} from "../../date";
import ViewJob from "./ViewJob";
import {JobTableFunctions} from "./Jobs";
import {AxiosResponse} from "axios";
import PaginationResponse from "../../controllers/PaginationResponse";
import PaginationRequestSearch from "../../controllers/PaginationRequestSearch";
import CreateJob, {JobStatus} from "./createjob";
import PagedSearchTable, {PagedTableFunctions, TableColumn} from "../../components/PagedSearchTable";
import CarbohydrateController from "../../controllers/CarbohydrateController";
import MfwSuccess from "../../controllers/MfwSuccess";
import MFWSuccessDialog from "../../components/MFWSuccessDialog";
import WarningPopup, {useWarningState} from "../../components/WarningPopup";
import AppContext from "../../appContext";
import {classNames, wrapLoader} from "../../wrapper";


export function useMountedPromise() {
    const mounted = useRef(false);

    function createPromise<T>(promise: Promise<T>): Promise<T> {
        return new Promise<T>((res, rej) => {
            promise.then(resp => {
                if (mounted.current)
                    res(resp);
            }).catch(err => {
                rej(err);
            })
        });
    }
    useEffect(() => {
        mounted.current = true;
        
        return () => {
            mounted.current = false;
        }
    }, []);

    return {
        createPromise,
        mounted
    }
}


const JobTable: React.FC<{
    completed: boolean,
    archived: boolean,
    canPost?: boolean,
    componentRef: MutableRefObject<JobTableFunctions | undefined>,
    call: (request: PaginationRequestSearch) => Promise<AxiosResponse<PaginationResponse<JobDisplay>>>
}> = (props) => {
    const pagedTableRef = useRef<PagedTableFunctions<JobDisplay>>();
    const context = useContext(AppContext);

    const [editJobId, setEditJobId] = useState<JobStatus>('closed')
    const [viewJobId, setViewJobId] = useState<number>()
    const [successData, setSuccessData] = useState<MfwSuccess[] | null>(null)
    const postMfwState = useWarningState<number>(0)

    React.useImperativeHandle(props.componentRef, () => ({
        refresh: () => pagedTableRef.current?.refresh()
    }))

    function checkPostToMfw(row: JobDisplay) {
        if (row.postedToMfw && row.hasMfwFailures) {
            postMfwState.show("Some samples failed in the previous post. Do you want to re-post to MFW?", row.id)
        } else if (row.postedToMfw) {
            postMfwState.show("Are you sure you want to post to MFW again?", row.id)
        } else {
            postMfwState.show("Are you sure you want to post to MFW?", row.id)
        }
    }

    function postToMfw(id: number) {
        wrapLoader(context, CarbohydrateController.postToMfwJob({id}), (data) => {
            setSuccessData(data)
            pagedTableRef.current?.refresh()
        })
    }

    const columns: TableColumn<JobDisplay>[] = [
        {
            header: 'Job',
            key: 'id',
            row: (row) => <>J{row.id}</>
        },
        {
            header: 'Entities',
            key: 'entity',
            row: (row) => row.entity
        },
        {
            header: 'Farms',
            key: 'farms',
            row: (row) => row.farms
        },
        {
            header: 'Samples',
            key: 'samples',
            row: (row) => {
                const parts = row.samples.split(", ").filter(s => s)
                if (parts.length === 0) return ''
                if (parts.length === 1) return parts[0]
                return `${parts[0]} to ${parts[parts.length - 1]}`
            }
        },
        {
            header: 'Progress',
            key: 'progress',
            row: (row) => <>{row.samplesCompleted}/{row.samplesTotal}</>
        },
        {
            header: 'Created',
            key: 'created',
            row: (row) => dateFormat(row.created)
        },
        {
            header: 'Created By',
            key: 'createdBy',
            row: (row) => row.createdBy
        },
        {
            header: 'Received',
            key: 'received',
            row: (row) => dateFormat(row.received)
        },
        ...(props.canPost ? [{
            header: 'Date Posted To MFW',
            key: 'postedToMfwDate',
            row: (row: JobDisplay) => <span>{dateFormat(row.postedToMfwDate, '%d %M %Y %H:%i') || "_"}</span>
        }] : []),
        {
            header: <div className='text-right'>Actions</div>,
            key: 'actions',
            row: (row) => <div className='flex items-center justify-end text-right'>
                {props.canPost && row.hasLocation
                    ? <div className={classNames("btn-sm", row.postedToMfw
                            ? (row.hasMfwFailures ? "bg-orange-500 text-white" : "bg-gray-400")
                            : "btn-primary")}
                           onClick={(e) => { e.stopPropagation(); checkPostToMfw(row) }}>
                        post to mfw
                    </div>
                    : null}
                <div className="btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); setViewJobId(row.id) }}>view</div>
                <div className="btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); setEditJobId(row.id == 0 ? 'create' : row.id) }}>edit</div>
            </div>
        },
    ]

    return (
        <div>
            <PagedSearchTable
                componentRef={pagedTableRef}
                call={props.call}
                keyExtractor={job => job.id}
                columns={columns}
            />

            {viewJobId ? <ViewJob id={viewJobId} refresh={() => pagedTableRef.current?.refresh()} setId={setViewJobId} completed={props.completed}/> : null}

            <CreateJob
                jobId={editJobId}
                setModelValue={show => {
                    if (show) return;

                    setEditJobId('closed')
                    pagedTableRef.current?.refresh()
            }} />

            <WarningPopup state={postMfwState} onYes={postToMfw}/>
            <MFWSuccessDialog data={successData} close={() => setSuccessData(null)}/>
        </div>
    )
}

export default JobTable;
